import type {
  ActionClassification,
  ActionItem,
  ActionPlan,
  ActionType,
  DiagnosisFinding,
} from './contracts';

const TYPE_BY_FINDING: Record<DiagnosisFinding['type'], ActionType> = {
  bottleneck: 'adjust_stage',
  risk: 'investigate',
  opportunity: 'test_conversion',
  data_gap: 'add_measurement',
  inconsistency: 'review_data',
  goal_gap: 'adjust_stage',
  efficiency: 'investigate',
};

export const ACTION_TEMPLATE_VERSION = '1.0.0';

function classifyImpact(finding: DiagnosisFinding): ActionClassification {
  if (finding.severity === 'critical' || (finding.impactScore ?? 0) >= 70) return 'high';
  if (finding.severity === 'attention' || finding.severity === 'positive') return 'low';
  return finding.evidence.length ? 'low' : 'unknown';
}

function classifyEffort(finding: DiagnosisFinding): ActionClassification {
  if (!finding.evidence.length) return 'unknown';
  if (finding.category === 'structure' || finding.category === 'automation') return 'high';
  if (finding.type === 'data_gap' || finding.type === 'inconsistency') return 'low';
  return finding.affectedNodeIds.length <= 1 && finding.affectedEdgeIds.length <= 1
    ? 'low'
    : 'high';
}

export function priorityFor(
  impact: ActionClassification,
  effort: ActionClassification,
): 1 | 2 | 3 | 4 | undefined {
  if (impact === 'unknown' || effort === 'unknown') return undefined;
  if (impact === 'high' && effort === 'low') return 1;
  if (impact === 'high' && effort === 'high') return 2;
  if (impact === 'low' && effort === 'low') return 3;
  return 4;
}

function toAction(finding: DiagnosisFinding): ActionItem {
  const impact = classifyImpact(finding);
  const effort = classifyEffort(finding);
  return {
    id: `action:${finding.id}`,
    title: finding.recommendation ?? `Investigate: ${finding.title}`,
    reason: finding.summary,
    actionType: finding.category === 'structure' ? 'fix_structure' : TYPE_BY_FINDING[finding.type],
    evidence: finding.evidence,
    affectedNodeIds: finding.affectedNodeIds,
    affectedEdgeIds: finding.affectedEdgeIds,
    impact,
    effort,
    urgency: 'unknown',
    ...(priorityFor(impact, effort) === undefined ? {} : { priority: priorityFor(impact, effort) }),
    caveats: [
      ...finding.caveats,
      'This action is advisory and does not mutate or execute the funnel.',
    ],
    sourceRuleIds: [finding.ruleId],
    sourceFindingIds: [finding.id],
  };
}

function deduplicate(actions: readonly ActionItem[]): ActionItem[] {
  const grouped = new Map<string, ActionItem>();
  for (const action of actions) {
    const key = `${action.actionType}:${[...action.affectedNodeIds].sort().join(',')}:${[...action.affectedEdgeIds].sort().join(',')}`;
    const previous = grouped.get(key);
    if (!previous) {
      grouped.set(key, action);
      continue;
    }
    grouped.set(key, {
      ...previous,
      evidence: [...previous.evidence, ...action.evidence].filter(
        (value, index, all) =>
          all.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(value)) ===
          index,
      ),
      caveats: [...new Set([...previous.caveats, ...action.caveats])],
      sourceRuleIds: [...new Set([...previous.sourceRuleIds, ...action.sourceRuleIds])],
      sourceFindingIds: [...new Set([...previous.sourceFindingIds, ...action.sourceFindingIds])],
    });
  }
  return [...grouped.values()];
}

export function buildActionPlan(
  findings: readonly DiagnosisFinding[],
  coverage: number,
): ActionPlan {
  const items = deduplicate(findings.map(toAction)).sort(
    (left, right) =>
      (left.priority ?? 99) - (right.priority ?? 99) ||
      left.sourceFindingIds[0]!.localeCompare(right.sourceFindingIds[0]!) ||
      left.id.localeCompare(right.id),
  );
  return {
    templateVersion: ACTION_TEMPLATE_VERSION,
    items,
    coverage,
    warnings: items.some((item) => item.priority === undefined)
      ? ['Some actions remain unranked because impact or effort evidence is insufficient.']
      : [],
    explanation:
      'Actions are mapped from deterministic findings and ordered by BR-132. Urgency remains unknown and has no priority effect.',
  };
}
